/**
 * DayModal
 * Create a block on a specific day
 */

import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Modal } from 'react-bootstrap';
import Form4 from 'components/interaction/Form4/index.jsx';
import { addMinutes, format } from 'utils/date-fns/index.js';
import messages from './messages.js';
import schema from './dayForm.js';

const ModalBody = styled(Modal.Body)`
  span.endtime-label  {
    width: 8rem;
  }
`;

function DayModal(props) {
  const { slotLength, numSlots = 4, onHide, value, onChange, timeOnly } = props;
  const defaultValue = {
    time: format(value, 'HH:mm'),
    slotCount: numSlots,
  };

  function getDate(time) {
    const result = new Date(value);
    const [hours, minutes] = time.split(':').map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
      throw new Error('Invalid time format');
    }
    result.setHours(hours);
    result.setMinutes(minutes);
    result.setSeconds(0);
    result.setMilliseconds(0);
    return result;
  }

  function getEndTime(values) {
    const { time, slotCount } = values;
    return addMinutes(getDate(time), slotLength * slotCount);
  }

  function handleSubmit(values) {
    const { time, slotCount } = values;
    const start = getDate(time);
    const end = getEndTime(values);
    const slots = Array(+slotCount).fill().map((v, idx) => addMinutes(start, idx * slotLength));
    const block = { start, end, slots };
    onHide();
    onChange(block);
  }

  function getFormattedEndTime(values) {
    try {
      const end = getEndTime(values);
      return format(end, 'HH:mm');
    } catch (e) {
      return '';
    }
  }

  return (
    <Modal size="sm" show onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>
          <FormattedMessage {...messages.addBlock} />
        </Modal.Title>
      </Modal.Header>
      <Form4
        onSubmit={handleSubmit}
        submitText={messages.next}
        schema={schema}
        horizontal
        defaultValues={defaultValue}
      >
        {({ render, values, action }) => (
          <>
            <ModalBody>
              <h5 className="mt-0 mb-4 text-capitalize">{format(value, 'PPPP')}</h5>
              <>
                {render('time')}
                {!timeOnly && render('slotCount')}
                <div className="d-flex mb-4">
                  <span className="endtime-label pe-4 text-end">
                    <FormattedMessage {...messages.endTime} />
                  </span>
                  <span>
                    {getFormattedEndTime(values)}
                  </span>
                </div>
              </>
            </ModalBody>
            <Modal.Footer>
              {action}
            </Modal.Footer>
          </>
        )}
      </Form4>
    </Modal>
  );
}

DayModal.propTypes = {
  slotLength: PropTypes.number.isRequired,
  numSlots: PropTypes.number,
  timeOnly: PropTypes.bool,
  onHide: PropTypes.func,
  onChange: PropTypes.func,
  value: PropTypes.object,
};

export default DayModal;
